import { useAnalytics } from '@cosuno/analytics-frontend';
import { PageLoader } from '@cosuno/cosuno-ui';
import { isCompanyFeatureEnabled } from '@cosuno/feature-flags-frontend';
import React, { useEffect } from 'react';

import { AUTH_STATUS } from './constants';
import {
  AuthContext,
  type AuthContextBase,
  isAnonymous,
  isAuthenticated,
} from './contexts/authContext';
import { useAuthentication } from './hooks/useAuthentication';

const AuthenticationRootContextProvider: React.FC<
  React.PropsWithChildren<{
    apiUserEndpoint: string;
    ipInfoToken: string | null;
    reCaptchaKey: string | null;
    isTestRun: boolean;
    isDevMode: boolean;
    routes: AuthContextBase['routes'];
  }>
> = ({ children, ipInfoToken, apiUserEndpoint, reCaptchaKey, isTestRun, isDevMode, routes }) => {
  const { initialize: initializeAnalytics } = useAnalytics();

  const authentication = useAuthentication({
    apiUserEndpoint,
    ipInfoToken,
    reCaptchaKey,
    isTestRun,
    routes,
  });

  const userCanBeTracked =
    isAnonymous(authentication) ||
    (isAuthenticated(authentication) &&
      isCompanyFeatureEnabled(authentication.authState.user.company, 'default-3rd-party-tracking'));
  const isTrackingAllowed = !isTestRun && userCanBeTracked;

  const enableAnalyticsDebugging = authentication.isSuperAdmin || isDevMode || isTestRun;

  useEffect(() => {
    initializeAnalytics({
      debuggingEnabled: enableAnalyticsDebugging,
      trackingEnabled: isTrackingAllowed,
    });
  }, [enableAnalyticsDebugging, initializeAnalytics, isTrackingAllowed]);

  return (
    <AuthContext.Provider value={authentication}>
      {authentication.authState.status === AUTH_STATUS.authenticating ? <PageLoader /> : children}
    </AuthContext.Provider>
  );
};

export default AuthenticationRootContextProvider;
